import React, {
  useState,
} from "react";

import {
  FaExternalLinkAlt,
  FaFilePdf,
  FaImage,
  FaLink,
  FaPlay,
} from "react-icons/fa";

import {
  getMediaThumbnailUrl,
  getYoutubeEmbedUrl,
  resolveTutorialMediaUrl,
} from "../utils/tutorialMediaUtils";

export default function TutorialMediaGallery({
  medias = [],
  fallbackImage = null,
  fallbackAlt = "",
  onOpenLightbox,
}) {
  const normalizedMedias =
    medias.length
      ? medias
      : fallbackImage
        ? [
            {
              id: `fallback-${fallbackImage}`,
              type: "image",
              url: fallbackImage,
              alt: fallbackAlt,
              caption:
                "Touchez pour agrandir",
            },
          ]
        : [];

  if (!normalizedMedias.length) {
    return null;
  }

  const images =
    normalizedMedias.filter(
      (media) =>
        media.type === "image"
    );

  const otherMedias =
    normalizedMedias.filter(
      (media) =>
        media.type !== "image"
    );

  return (
    <div className="mt-6 space-y-4">
      {images.length ? (
        <ImageGallery
          images={images}
          onOpenLightbox={
            onOpenLightbox
          }
        />
      ) : null}

      {otherMedias.map((media) => (
        <MediaItem
          key={media.id || media.url}
          media={media}
        />
      ))}
    </div>
  );
}

function ImageGallery({
  images,
  onOpenLightbox,
}) {
  return (
    <div
      className={`grid gap-3 ${
        images.length > 1
          ? "grid-cols-1 sm:grid-cols-2"
          : "grid-cols-1"
      }`}
    >
      {images.map(
        (media, index) => (
          <TutorialImage
            key={media.id || media.url}
            media={media}
            single={
              images.length === 1
            }
            onClick={() =>
              onOpenLightbox?.(
                images,
                index
              )
            }
          />
        )
      )}
    </div>
  );
}

function TutorialImage({
  media,
  single,
  onClick,
}) {
  const [hasError, setHasError] =
    useState(false);

  const imageUrl =
    getMediaThumbnailUrl(media);

  if (!imageUrl || hasError) {
    return (
      <div className="flex min-h-36 items-center justify-center rounded-[22px] border border-dashed border-slate-300 bg-slate-50 p-6 text-center dark:border-slate-700 dark:bg-slate-800/50">
        <div>
          <FaImage className="mx-auto text-2xl text-slate-400" />

          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            Image indisponible
          </p>
        </div>
      </div>
    );
  }

  return (
    <figure>
      <button
        type="button"
        onClick={onClick}
        className={`group relative flex w-full items-center justify-center overflow-hidden rounded-[22px] border border-slate-200 bg-slate-100 transition hover:border-violet-300 hover:shadow-xl dark:border-slate-700 dark:bg-slate-950 ${
          single
            ? "min-h-[220px] p-2 sm:min-h-[320px]"
            : "min-h-[180px] p-2"
        }`}
      >
        <img
          src={imageUrl}
          alt={
            media.alt ||
            "Illustration du tutoriel"
          }
          onError={() =>
            setHasError(true)
          }
          className={`w-full rounded-[16px] object-contain transition duration-300 group-hover:scale-[1.015] ${
            single
              ? "max-h-[560px]"
              : "h-56"
          }`}
          loading="lazy"
        />

        <span className="absolute bottom-4 right-4 inline-flex items-center gap-2 rounded-xl bg-slate-950/85 px-3 py-2 text-xs font-semibold text-white opacity-0 shadow-lg backdrop-blur transition group-hover:opacity-100">
          <FaImage />
          Agrandir
        </span>
      </button>

      {media.caption ? (
        <figcaption className="mt-2 text-center text-xs text-slate-500 dark:text-slate-400">
          {media.caption}
        </figcaption>
      ) : null}
    </figure>
  );
}

function MediaItem({
  media,
}) {
  const url =
    resolveTutorialMediaUrl(
      media.url
    );

  if (!url) {
    return null;
  }

  if (media.type === "youtube") {
    const embedUrl =
      getYoutubeEmbedUrl(url);

    if (embedUrl) {
      return (
        <div className="overflow-hidden rounded-[22px] border border-slate-200 bg-black shadow-sm dark:border-slate-700">
          <div className="aspect-video">
            <iframe
              src={embedUrl}
              title={
                media.alt ||
                "Vidéo du tutoriel"
              }
              className="h-full w-full"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>

          {media.caption ? (
            <div className="bg-white px-4 py-3 text-xs text-slate-500 dark:bg-slate-900 dark:text-slate-400">
              {media.caption}
            </div>
          ) : null}
        </div>
      );
    }
  }

  if (media.type === "video") {
    return (
      <figure>
        <video
          controls
          preload="metadata"
          poster={
            media.thumbnailUrl
              ? resolveTutorialMediaUrl(
                  media.thumbnailUrl
                )
              : undefined
          }
          className="max-h-[580px] w-full rounded-[22px] border border-slate-200 bg-black shadow-sm dark:border-slate-700"
        >
          <source src={url} />

          Votre navigateur ne permet
          pas de lire cette vidéo.
        </video>

        {media.caption ? (
          <figcaption className="mt-2 text-center text-xs text-slate-500 dark:text-slate-400">
            {media.caption}
          </figcaption>
        ) : null}
      </figure>
    );
  }

  const isPdf =
    media.type === "pdf";

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`group flex items-center gap-4 rounded-[20px] border p-4 transition ${
        isPdf
          ? "border-rose-200 bg-rose-50 hover:border-rose-300 dark:border-rose-900/40 dark:bg-rose-950/20"
          : "border-sky-200 bg-sky-50 hover:border-sky-300 dark:border-sky-900/40 dark:bg-sky-950/20"
      }`}
    >
      <div
        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-lg text-white ${
          isPdf
            ? "bg-rose-600"
            : "bg-sky-600"
        }`}
      >
        {isPdf ? (
          <FaFilePdf />
        ) : (
          <FaLink />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="font-semibold text-slate-900 dark:text-white">
          {media.alt ||
            (isPdf
              ? "Consulter le document"
              : "Consulter le lien")}
        </div>

        {media.caption ? (
          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {media.caption}
          </div>
        ) : null}
      </div>

      <FaExternalLinkAlt className="shrink-0 text-slate-400 transition group-hover:text-slate-700 dark:group-hover:text-white" />
    </a>
  );
}